import React from 'react';
import { CerebroDrawer, CerebroHeading, CerebroIcon, CerebroText, CerebroTextButton } from '../../shared/components';
import {
	ESignDocument,
	ESignDocumentEventTypeMap,
	ESignDocumentStateMap,
	ESignDocumentStatusMap,
} from '../../shared/models/ESignDocument';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { DocumentStatusDot } from '../../shared/components/DocumentStatusDot';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { useApi } from '../../shared/context';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useSnackbar } from 'notistack';
import { QueryKeys } from '../../shared/hooks';
import { getApiResponseData } from '../../shared/utils/axios';

interface ViewContractSignStatusDrawerProps {
	dealGuid: string | undefined;
	companyGuid: string | undefined;
	companySubscriptionGuid: string | undefined;
	productSubscriptionGuid: string | undefined;
	eSignDoc: ESignDocument | null | undefined;
	showUpdateStatusButton: boolean;
	onClose: () => void;
	open: boolean;
}

export const ViewContractSignStatusDrawer: React.FC<ViewContractSignStatusDrawerProps> = ({
	dealGuid,
	companyGuid,
	companySubscriptionGuid,
	productSubscriptionGuid,
	eSignDoc: doc,
	showUpdateStatusButton,
	open,
	onClose,
}) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const api = useApi();
	const client = useQueryClient();
	const snackbar = useSnackbar();

	const endpoint = !!doc?.productSubscriptionId
		? CerebroApiEndpoints.Deals.AgreementStatus(dealGuid, productSubscriptionGuid ?? undefined, doc?.agreementId)
		: CerebroApiEndpoints.Company.AgreementStatus(
				companyGuid,
				companySubscriptionGuid ?? undefined,
				doc?.agreementId
		  );

	const statusUpdateMutation = useMutation(
		async () => {
			const { data, error } = await getApiResponseData<void>(api.get(endpoint));

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.Root(dealGuid));
				snackbar.enqueueSnackbar('Contract status updated.', { variant: 'success' });
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar('There was a problem updating the contract status.', { variant: 'error' });
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} xl={true} onClose={onClose}>
			<CerebroHeading>View Contract Sign Status</CerebroHeading>

			<CerebroText sx={{ marginBottom: 2 }}>
				<>
					<DocumentStatusDot state={doc?.state} sx={{ marginRight: 1 }} />
					{doc ? ESignDocumentStateMap[doc.state] : ''}
				</>
			</CerebroText>

			<CerebroText>
				<b>Customer:</b> {doc?.eSignDocumentActivityLogs?.find(l => !!l.customerEmail)?.customerEmail}
			</CerebroText>
			<CerebroText>
				<b>Sender:</b> {doc?.senderName} ({doc?.senderEmail})
			</CerebroText>

			{showUpdateStatusButton && (
				<CerebroTextButton sx={{ float: 'right' }} onClick={() => statusUpdateMutation.mutate()}>
					<CerebroIcon padRight>
						<FontAwesomeIcon icon={faSync} size="sm" />
					</CerebroIcon>
					Update Status
				</CerebroTextButton>
			)}

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Event</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Customer Status</TableCell>
							<TableCell>Cerebro Status</TableCell>
							<TableCell>Date</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<>
							{doc?.eSignDocumentActivityLogs && doc?.eSignDocumentActivityLogs.length > 0 && (
								<>
									{doc?.eSignDocumentActivityLogs
										?.sort(
											(a, b) =>
												new Date(b.createdOn!).getTime() - new Date(a.createdOn!).getTime()
										)
										.map(log => {
											return (
												<TableRow key={log.eSignDocumentActivityLogId}>
													<TableCell>{ESignDocumentEventTypeMap[log.eventType!]}</TableCell>
													<TableCell>
														{ESignDocumentStatusMap[log.agreementStatus!]}
													</TableCell>
													<TableCell>{log.customerStatus}</TableCell>
													<TableCell>{log.cerebroStatus}</TableCell>
													<TableCell>{log.createdOnLocalTime}</TableCell>
												</TableRow>
											);
										})}
								</>
							)}

							{!doc?.eSignDocumentActivityLogs ||
								(doc?.eSignDocumentActivityLogs.length == 0 && (
									<TableRow>
										<TableCell colSpan={4}>There are no activity logs for this document</TableCell>
									</TableRow>
								))}
						</>
					</TableBody>
				</Table>
			</TableContainer>
		</CerebroDrawer>
	);
};
